import type { CSSProperties } from 'react';

import EmailInput from './EmailInput';
import LongText from './LongText';
import MultipleChoice from './MultipleChoice';
import Select from './Select';
import ShortText from './ShortText';
import { buttonStyle, questionStyle } from './Survey.css';
import { type Question, QuestionTypes, type Survey } from './Survey.types';

interface Props {
  survey: Survey;
  email: string;
  isSubmitting: boolean;
  hasLocalStorageEmail: boolean;
  submittingText?: string;
  ctaText?: string;
}

const SurveyRenderer = ({
  survey,
  email,
  isSubmitting,
  submittingText = 'Submitting...',
  ctaText = 'Submit',
  hasLocalStorageEmail,
}: Props) => {
  if (!survey) return null;

  return (
    <div
      className="relative flex flex-col w-full"
      style={{
        gap: 'var(--gap)',
      }}
    >
      {/** We only prompt for email if there is no email in local storage, we make the assumption this user has signed up if it is in local storage */}
      {!hasLocalStorageEmail && <EmailInput email={email} />}
      {survey.form_questions.map((question: Question) => {
        const isFreeForm = question.question_type === QuestionTypes.FREE_FORM;
        const isMultipleChoice = question.question_type === QuestionTypes.MULTIPLE_CHOICE;
        const isLongText = question.question_type === QuestionTypes.LONG_TEXT;
        const isDropdown = question.question_type === QuestionTypes.DROPDOWN;

        // this shouldn't happen but if there's a free form question, we should always have a custom field
        // however, we've seen instances of custom_field missing which results in an error
        if (isFreeForm && !question.custom_field) {
          return null;
        }

        return (
          <div
            key={question.id}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              textAlign: 'var(--text-align)' as CSSProperties['textAlign'],
            }}
          >
            <label className={questionStyle} htmlFor={question.id}>
              {question.prompt}
              {question.required && <span style={{ color: 'var(--question-required-color)' }}>&#42;</span>}
            </label>
            <div>
              {isLongText && <LongText question={question} />}
              {isFreeForm && (
                <ShortText name={question.id} required={question.required} type={question.custom_field.kind} />
              )}
              {isMultipleChoice && <MultipleChoice question={question} />}
              {isDropdown && <Select name={question.id} options={question.form_question_options} />}
            </div>
          </div>
        );
      })}
      <input
        type="submit"
        className={buttonStyle}
        disabled={isSubmitting}
        value={isSubmitting ? submittingText : ctaText}
      />
    </div>
  );
};

export default SurveyRenderer;
